import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../api";

const STATUSES = [
  ["PENDING", "입금 대기"],
  ["PAID", "입금 확인"],
  ["SHIPPED", "배송 중"],
  ["DELIVERED", "배송 완료"],
  ["CANCELLED", "취소"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}
const won = (n) => `${(n || 0).toLocaleString("ko-KR")}원`;

/* 펌웨어 개발의뢰 — 라오나봇 내 프로젝트에서 [펌웨어 빌드 포함]으로 주문한 의뢰 (백엔드 app/laona_fw.py).
   입금 확인(PAID 이후)으로 바꾸면 라오나빌드서버의 [빌드요청 가져오기]가 이 의뢰를 가져간다 */
const FW_READY = ["PAID", "SHIPPED", "DELIVERED"];

function FwPanel({ order, fw, onChanged }) {
  const [md, setMd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggleMd() {
    if (md !== null) { setMd(null); return; }
    setError("");
    try {
      const res = await fetch(`/api/sstoa/shop-orders/fw/${encodeURIComponent(fw.request_no)}/request.md`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("의뢰서를 불러오지 못했습니다.");
      setMd(await res.text());
    } catch (err) { setError(err.message); }
  }

  async function setStatus(to, label) {
    if (!window.confirm(`${fw.request_no} 의뢰를 '${label}'(으)로 바꿀까요?`)) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/shop-orders/fw/${encodeURIComponent(fw.request_no)}/status`, { to });
      await onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  const ready = FW_READY.includes(order.status);
  const where = fw.status === "ORDERED"
    ? (ready ? "입금 확인됨 — 라오나빌드서버에서 [빌드요청 가져오기]로 가져갈 수 있습니다."
      : order.status === "CANCELLED" ? "주문이 취소되었습니다." : "입금 확인 전 — 입금 확인으로 바꾸면 빌드 서버가 가져갈 수 있습니다.")
    : fw.status === "RECEIVED" ? `빌드 서버가 가져갔습니다${fw.received_at ? ` (${formatWhen(fw.received_at)})` : ""}.`
    : "";
  return (
    <div className="sstoa-fw">
      <div className="sstoa-fw__head">
        <span className="sstoa-tag sstoa-tag--fw">펌웨어 개발의뢰</span>
        <b style={{ fontFamily: "ui-monospace, monospace" }}>{fw.request_no}</b>
        <span>{fw.title}</span>
        <span className="sstoa-tag">{fw.label}</span>
        <span className="sstoa-hint">모듈 {fw.modules} · 연결 {fw.connections} · 빌드비 {won(fw.fee)}</span>
      </div>
      {where && <p className="sstoa-hint" style={{ margin: "6px 0" }}>{where}</p>}
      <div className="sstoa-actions">
        <button className="sstoa-btn sstoa-btn--sm" onClick={toggleMd}>{md === null ? "기능정의서 보기" : "기능정의서 닫기"}</button>
        {["RECEIVED", "IN_PROGRESS"].includes(fw.status) && (
          <>
            {fw.status === "RECEIVED" && (
              <button className="sstoa-btn sstoa-btn--sm" disabled={busy} onClick={() => setStatus("IN_PROGRESS", "개발 중")}>개발 중으로</button>
            )}
            <button className="sstoa-btn sstoa-btn--sm sstoa-btn--primary" disabled={busy} onClick={() => setStatus("DONE", "완료")}>완료로</button>
          </>
        )}
      </div>
      {fw.admin_note && <pre className="sstoa-fw__log">{fw.admin_note}</pre>}
      {md !== null && <pre className="sstoa-fw__md">{md}</pre>}
      {error && <div className="sstoa-error" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}

function Row({ item, onChange, onReload }) {
  const [open, setOpen] = useState(item.status === "PENDING");
  const [tracking, setTracking] = useState(item.tracking || "");
  const [memo, setMemo] = useState(item.admin_memo || "");
  const [busy, setBusy] = useState(false);

  async function patch(p, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try { await onChange(item.id, p); } finally { setBusy(false); }
  }

  const first = item.items[0];
  const more = item.items.length - 1;

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={() => setOpen((v) => !v)}>
        <td>
          <span className={`sstoa-tag ${["DELIVERED", "CANCELLED"].includes(item.status) ? "sstoa-tag--gray" : ""}`}>
            {STATUS_LABEL[item.status] || item.status}
          </span>
        </td>
        <td style={{ fontFamily: "ui-monospace, monospace" }}>#{item.id}</td>
        <td style={{ fontWeight: 600 }}>
          {item.fw && item.fw.length > 0 && <span className="sstoa-tag sstoa-tag--fw" style={{ marginRight: 6 }}>펌웨어 개발 포함</span>}
          {first ? first.name : "—"}{more > 0 ? ` 외 ${more}건` : ""}
        </td>
        <td>
          {item.receiver_name}
          <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>입금자 {item.depositor}</div>
        </td>
        <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{won(item.total)}</td>
        <td>{formatWhen(item.created_at)}</td>
      </tr>

      {open && (
        <tr>
          <td colSpan={6} style={{ background: "rgba(255,255,255,0.02)" }}>
            <div style={{ padding: "6px 2px 14px" }}>
              <table className="sstoa-table" style={{ marginBottom: 12 }}>
                <tbody>
                  {item.items.map((i) => (
                    <tr key={i.slug}>
                      <td style={{ fontFamily: "ui-monospace, monospace" }}>{i.code}</td>
                      <td>{i.name}</td>
                      <td style={{ textAlign: "right" }}>{won(i.price)} × {i.qty}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{won(i.price * i.qty)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      상품{item.fw && item.fw.length ? " · 펌웨어 개발" : ""} {won(item.subtotal)} + 배송비 {item.shipping ? won(item.shipping) : "무료"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{won(item.total)}</td>
                  </tr>
                </tbody>
              </table>

              {(item.fw || []).map((f) => <FwPanel key={f.request_no} order={item} fw={f} onChanged={onReload} />)}

              <div className="sstoa-meta" style={{ marginBottom: 12, flexWrap: "wrap" }}>
                <span>회원 {item.member ? `${item.member.name} (${item.member.email})` : "—"}</span>
                <span>받는 분 {item.receiver_name} · {item.phone} · {item.email}</span>
                <span>입금자명 {item.depositor}</span>
                <span>재고 {item.stock_taken ? "차감됨" : "차감 전"}</span>
              </div>
              <div style={{ marginBottom: 12, lineHeight: 1.7 }}>
                배송지 {item.zip ? `(${item.zip}) ` : ""}{item.address} {item.address2}
                {item.memo && <div>메모 {item.memo}</div>}
              </div>

              <div className="sstoa-row" style={{ marginBottom: 12 }}>
                <div className="sstoa-field">
                  <label>송장 · 택배사</label>
                  <input className="sstoa-input" value={tracking} onChange={(e) => setTracking(e.target.value)} disabled={busy} placeholder="CJ대한통운 1234-5678-9012" />
                </div>
                <div className="sstoa-field" style={{ flex: 2 }}>
                  <label>처리 메모 <span className="sstoa-hint">— 회원에게는 보이지 않습니다</span></label>
                  <input className="sstoa-input" value={memo} onChange={(e) => setMemo(e.target.value)} disabled={busy} />
                </div>
              </div>

              <div className="sstoa-actions">
                <button className="sstoa-btn sstoa-btn--sm" onClick={() => patch({ tracking, admin_memo: memo })} disabled={busy}>
                  송장 · 메모 저장
                </button>
                <span className="sstoa-hint" style={{ margin: "0 4px" }}>상태 변경</span>
                {STATUSES.map(([v, l]) => (
                  <button
                    key={v} disabled={busy || item.status === v}
                    className={`sstoa-btn sstoa-btn--sm${item.status === v ? " sstoa-btn--primary" : ""}${v === "CANCELLED" ? " sstoa-btn--danger" : ""}`}
                    onClick={() => patch(
                      v === "SHIPPED" ? { status: v, tracking } : { status: v },
                      v === "CANCELLED" ? `주문 #${item.id} 을(를) 취소할까요?${item.stock_taken ? " 뺐던 재고는 되돌립니다." : ""}` : null,
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <p className="sstoa-hint" style={{ marginTop: 8 }}>
                입금 확인으로 바꾸면 재고가 빠지고, 취소하면 되돌아갑니다. 배송 완료된 주문은 회원 MY 페이지의 구매내역으로 옮겨집니다.
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ShopOrdersAdmin() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");

  async function load(status = filter) {
    setError("");
    try {
      const [list, c] = await Promise.all([
        api.get(`/shop-orders${status ? `?status=${status}` : ""}`),
        api.get("/shop-orders/counts"),
      ]);
      setRows(list);
      setCounts(c);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilter(s) { setFilter(s); load(s); }

  async function change(id, patch) {
    try {
      await api.post(`/shop-orders/${id}`, patch);
      await load();
    } catch (err) { setError(err.message); }
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">판매 주문</h1>
          <p className="sstoa-sub">홈페이지 판매제품 주문입니다. 입금을 확인하면 상태를 바꿔 주세요. 행을 누르면 내용이 펼쳐집니다.</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("")}>
          전체{counts ? ` ${counts.total}` : ""}
        </button>
        {STATUSES.map(([v, l]) => (
          <button key={v} className={`sstoa-btn sstoa-btn--sm${filter === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter(v)}>
            {l}{counts ? ` ${counts[v] || 0}` : ""}
          </button>
        ))}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">주문이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>상태</th><th>주문</th><th>상품</th><th>받는 분</th><th>금액</th><th>주문일</th></tr>
            </thead>
            <tbody>
              {rows.map((it) => <Row key={it.id} item={it} onChange={change} onReload={() => load()} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
